/**
 * React Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 */
import React from 'react';
import { Alert, Container, Button } from 'react-bootstrap';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null,
      retryKey: 0  // Add retry key for forcing re-render
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2)
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging (no sensitive data)
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log to console in development mode only
    if (process.env.NODE_ENV === 'development') {
      console.error('Error Boundary caught an error:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        errorId: this.state.errorId,
        timestamp: new Date().toISOString()
      });
    }

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null,
      retryKey: this.state.retryKey + 1  // Increment retry key to force re-render
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry);
      }

      // Default fallback UI (simple i18n using localStorage)
      const lang = (typeof window !== 'undefined' && (localStorage.getItem('i18n_lang') || 'en')) || 'en';
      const L = (en, zh) => (lang === 'zh' ? zh : en);
      return (
        <Container className="mt-4">
          <Alert variant="danger">
            <h2 className="alert-heading h4" role="heading" aria-level="2">{L('Something went wrong', '发生错误')}</h2>
            <p>{L('An unexpected error occurred while rendering this component. This error has been logged for investigation.', '渲染组件时发生了意外错误。该错误已记录用于排查。')}</p>
            
            {this.state.errorId && (
              <div className="mt-3 small text-muted">{L('Error ID:', '错误ID：')} <code>{this.state.errorId}</code></div>
            )}

            <div className="mt-3">
              <Button variant="outline-primary" onClick={this.handleRetry}>{L('Try Again','重试')}</Button>
              {this.props.onReload && (
                <Button 
                  variant="outline-secondary" 
                  className="ms-2"
                  onClick={() => window.location.reload()}
                >
                  {L('Reload Page','刷新页面')}
                </Button>
              )}
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-3">
                <summary style={{ cursor: 'pointer' }}>{L('Developer Details','开发者详情')}</summary>
                <div className="mt-2">
                  <strong>{L('Error:','错误：')}</strong>
                  <pre className="small bg-light p-2 mt-1">
                    {this.state.error.toString()}
                  </pre>
                  
                  <strong>{L('Stack Trace:','堆栈信息：')}</strong>
                  <pre className="small bg-light p-2 mt-1">
                    {this.state.error.stack}
                  </pre>
                  
                  {this.state.errorInfo.componentStack && (
                    <>
                      <strong>{L('Component Stack:','组件栈：')}</strong>
                      <pre className="small bg-light p-2 mt-1">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </div>
              </details>
            )}
          </Alert>
        </Container>
      );
    }

    // Add key to force re-render of children after retry
    if (React.Children.count(this.props.children) === 1) {
      return React.cloneElement(this.props.children, { key: this.state.retryKey });
    }
    
    // For multiple children, wrap in a div with key
    return (
      <div key={this.state.retryKey}>
        {this.props.children}
      </div>
    );
  }
}

export default ErrorBoundary;
